import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Check, Sparkles, Info } from 'lucide-react';
import confetti from 'canvas-confetti';
import { TYPE_LABELS } from '../../data/roadmapData';
import { isFieldValid, fieldHint } from '../../data/roadmapValidation';

function Field({ field, value, onChange }) {
  const common = {
    id: field.key,
    value: field.kind === 'checkbox' ? undefined : value ?? '',
    onChange: (e) =>
      onChange(
        field.kind === 'checkbox' ? e.target.checked : e.target.value
      ),
  };

  return (
    <div className="rm-field">
      {field.kind !== 'checkbox' && (
        <label className="rm-field-label" htmlFor={field.key}>
          {field.label}
        </label>
      )}

      {field.kind === 'textarea' && (
        <textarea className="rm-input rm-textarea" rows={6} {...common} />
      )}
      {field.kind === 'text' && (
        <input type="text" className="rm-input" {...common} />
      )}
      {field.kind === 'number' && (
        <div className="rm-number-wrap">
          <input type="number" inputMode="numeric" className="rm-input" {...common} />
          {field.unit && <span className="rm-unit">{field.unit}</span>}
        </div>
      )}
      {field.kind === 'date' && (
        <input type="date" className="rm-input" {...common} />
      )}
      {field.kind === 'select' && (
        <select className="rm-input rm-select" {...common}>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )}
      {field.kind === 'checkbox' && (
        <label className="rm-checkbox">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span className="rm-checkbox-box">
            {value === true && <Check size={16} strokeWidth={3} />}
          </span>
          <span>{field.label}</span>
        </label>
      )}
    </div>
  );
}

export default function ItemScreen({
  level,
  item,
  index,
  total,
  answers,
  onChangeField,
  onValidate,
  onBack,
}) {
  const [confirming, setConfirming] = useState(false);
  const itemAnswers = answers[item.id] || {};

  const allValid = item.fields.every((f) =>
    isFieldValid(f, itemAnswers[f.key], answers)
  );

  const handleValidate = useCallback(() => {
    onValidate();
    confetti({
      particleCount: 60,
      spread: 65,
      origin: { y: 0.7 },
      colors: ['#8A9A82', '#C9A86A', '#C08457', '#E8DFD3'],
      scalar: 0.9,
    });
  }, [onValidate]);

  return (
    <div className="rm-item-screen">
      <div className="rm-item-top">
        <button className="rm-back" onClick={onBack} aria-label="Retour">
          <ChevronLeft size={22} />
        </button>
        <div className="rm-item-progress">
          <span className="rm-item-step">
            Item {index + 1}/{total}
          </span>
          <div className="rm-bar rm-bar-sm">
            <div
              className="rm-bar-fill"
              style={{ width: `${((index + 1) / total) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="rm-daily-hint">1 étape par jour suffit 🌱</div>

      <AnimatePresence mode="wait">
        <motion.div
          key={item.id}
          className="rm-item-body"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
        >
          <span className="rm-item-type">
            Niveau {level.id} · {TYPE_LABELS[item.type]}
          </span>
          <h2 className="rm-item-title">{item.title}</h2>
          <p className="rm-item-question">{item.question}</p>

          {item.help && (
            <div className="rm-item-help">
              <Info size={15} strokeWidth={1.8} />
              <span>{item.help}</span>
            </div>
          )}

          <div className="rm-fields">
            {item.fields.map((f) => {
              const val = itemAnswers[f.key];
              const valid = isFieldValid(f, val, answers);
              const hint = !valid ? fieldHint(f, val, answers) : '';
              return (
                <div key={f.key}>
                  <Field
                    field={f}
                    value={val}
                    onChange={(v) => onChangeField(f.key, v)}
                  />
                  {hint && <p className="rm-field-hint">{hint}</p>}
                </div>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="rm-item-footer">
        <button
          className="rm-btn rm-btn-primary"
          disabled={!allValid}
          onClick={() => setConfirming(true)}
        >
          Valider
        </button>
      </div>

      {/* Étape de confirmation = engagement */}
      <AnimatePresence>
        {confirming && (
          <motion.div
            className="rm-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setConfirming(false)}
          >
            <motion.div
              className="rm-modal"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="rm-modal-icon">
                <Sparkles size={24} strokeWidth={1.6} />
              </div>
              <h3 className="rm-modal-title">Tu valides cette réponse ?</h3>
              <p className="rm-modal-text">
                Une fois validée, elle devient un engagement. Tu pourras
                toujours y revenir.
              </p>
              <div className="rm-modal-actions">
                <button
                  className="rm-btn rm-btn-ghost"
                  onClick={() => setConfirming(false)}
                >
                  Ajuster
                </button>
                <button
                  className="rm-btn rm-btn-primary"
                  onClick={() => {
                    setConfirming(false);
                    handleValidate();
                  }}
                >
                  Oui, je valide
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
